import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const SEVERITY_ICON = {
    success: CheckCircleOutlineOutlinedIcon,
    error: ErrorOutlineOutlinedIcon,
    warning: WarningAmberOutlinedIcon,
    info: InfoOutlinedIcon,
};

/**
 * DialogTitle (renders as <h2>) with an optional severity icon on the left
 * and a close (×) icon on the right.
 *
 * Props:
 *   severity   — 'success' | 'error' | 'warning' | 'info' (optional). Drives the
 *                leading icon and the title's tinted background. Colors come from
 *                the theme palette (theme.palette[severity].main / .light /
 *                .contrastText) — override per-app via the theme.
 *   onClose    — handler for the × icon. Wire to the same action as the
 *                existing "Close" / "OK" button.
 */
const DialogTitleWithClose = ({ id, children, onClose, severity, sx, dataAutomationId, ...rest }) => {
    const Icon = severity ? SEVERITY_ICON[severity] : null;
    const testId = dataAutomationId || id;

    return (
        <DialogTitle
            id={id}
            sx={[
                { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 },
                sx,
            ].filter(Boolean)}
            {...(testId ? { 'data-automation-id': testId } : {})}
            {...rest}
        >
            {Icon && (
                <Icon aria-hidden="true" sx={{ color: `${severity}.main`, fontSize: '1.5rem', flexShrink: 0 }} />
            )}
            {children}
            {onClose && (
                <IconButton aria-label="close" onClick={onClose} sx={{ ml: 'auto' }}
                    data-automation-id={testId ? `${testId}_close_btn` : undefined}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            )}
        </DialogTitle>
    );
};

export default DialogTitleWithClose;
